import { AddPreClosureBanner, InformationActions, InformationActionTypes, LoadBannersSuccess } from '@core/store/actions/information.actions';
import { InformationStructure } from 'app/models/information-structure.model';
import { BannerMessage, Message } from '@model';
import { InformationKey } from 'app/models/enums/information-key.enum';
import { Action, ActionReducer } from '@ngrx/store';

export interface State {
  loaded: boolean;
  loading: boolean;
  bannerMessagesLoaded: boolean;
  bannerMessagesLoading: boolean;
  bannerMessages: BannerMessage[];
  startPageBanner: BannerMessage;
  preClosureBanner: BannerMessage;
  contextualBannerMessages: BannerMessage[],
  data: InformationStructure;
}

export const initialState: State = {
  loaded: false,
  loading: false,
  bannerMessagesLoaded: false,
  bannerMessagesLoading: false,
  bannerMessages: [],
  startPageBanner: null,
  preClosureBanner: null,
  contextualBannerMessages: [],
  data: null
};

export function stateReducer(state = initialState, action: InformationActions): State {

  switch (action.type) {
    case InformationActionTypes.LoadData:
      return { ...state, loaded: false, loading: true };
    case InformationActionTypes.LoadDataSuccess:
      return {
        ...state,
        loaded: true,
        loading: false,
        data: {
          ...action.payload,
          informationGroup: {
            ...action.payload.informationGroup,
            expanded:
              action.payload.informationGroup.informations.some(o => o.expanded) ||
              action.payload.informationGroup.informations.length > 0
          }
        }
      };
    case InformationActionTypes.LoadDataFail:
      return { ...state, loaded: false, loading: false };

    case InformationActionTypes.UpdateItem:
      const newItem = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          informationGroup: {
            ...state.data.informationGroup,
            informations: state.data.informationGroup.informations.map(item =>
              item.id === newItem.id ? newItem : item
            )
          }
        }
      };

    case InformationActionTypes.UpdateGroup:
      const newGroup = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          informationGroup: {
            ...state.data.informationGroup,
            expanded: newGroup.expanded
          }
        }
      };

    case InformationActionTypes.LoadBanners:
        return { ...state, bannerMessagesLoaded: false, bannerMessagesLoading: true };

    case InformationActionTypes.LoadBannersSuccess:
      return {
        ...state,
        bannerMessages: action.payload.filter(message => !message.route),
        contextualBannerMessages: action.payload.filter(message => message.route).map(message => message.route ? {...message, isVisible: true}: message),
        bannerMessagesLoaded: true,
        bannerMessagesLoading: false
      };
    case InformationActionTypes.LoadBannersFail:
        return { ...state, bannerMessagesLoaded: false, bannerMessagesLoading: false };
    case InformationActionTypes.AddStartPageBanner:
      return { ...state, startPageBanner: action.payload };
    case InformationActionTypes.AddPreClosureBanner:
      return {...state, preClosureBanner: action.payload };
    case InformationActionTypes.DismissPreClosureBanner: 
      return {...state, preClosureBanner: {...state.preClosureBanner, isVisible: false } };
    case InformationActionTypes.DismissBanner:
      return { ...state,
        bannerMessages: state.bannerMessages.map(message => message.id === action.payload ? {...message, isVisible: false } : message)
      };
    case InformationActionTypes.ShowBanners:

        return {...state,
          bannerMessages: state.bannerMessages.map(message => message?.isVisible ? message : {...message, isVisible: true }),
          preClosureBanner: state.preClosureBanner ? {...state.preClosureBanner, isVisible: true } : null
        };
    }
    return state; 
}

// --- Keep BannerMessages and PreClosureMessage State in SessonStorage - Persist on browser refresh
export function persistStateReducer(_reducer: ActionReducer<State>) {

  return (_state: State | undefined, _action: Action) => {
    const nextState = _reducer(_state, _action);
    return nextState;
  };
}
function getSessionState(storageKey: string): any {
  return JSON.parse(sessionStorage.getItem(storageKey));
}

// the keys from state which we'd like to save.
// const stateKeys = ['layout.theme'];
// the key for the local storage.
const banner_message_ids = '__bannerMessageIds';
const pre_closure_message_id = '__preClosureMessageId';

export function updateStateReducer(_reducer: ActionReducer<State>) {
  return (_state: State | undefined, _action: Action) => {
   
    if (_action.type === InformationActionTypes.LoadBannersSuccess) {
      // console.log('state', _state);
      // console.log('action', _action);

      const payload = (<any>_action).payload;
      const savedSessionState = getSessionState(banner_message_ids);
      if (savedSessionState) {
        const mergedPayload = mergePayloadArray(payload, savedSessionState);
        const mergedAction = { type: _action.type, payload: mergedPayload } as unknown as LoadBannersSuccess;

        return _reducer(_state, mergedAction);
      }
      return _reducer(_state, _action);
    }
    if (_action.type === InformationActionTypes.DismissBanner) {

      const nextState = _reducer(_state, _action);
      const msgId = (<any>_action).payload;
      const savedSessionState = getSessionState(banner_message_ids);
      const updatedSessionState = updateState(nextState, savedSessionState, msgId);
      sessionStorage.setItem(banner_message_ids, JSON.stringify(updatedSessionState));
 
      return _reducer(_state, _action);
    }
    if (_action.type === InformationActionTypes.ShowBanners) {

      sessionStorage.setItem(banner_message_ids, JSON.stringify(''));
      sessionStorage.setItem(pre_closure_message_id, JSON.stringify(''));

      return _reducer(_state, _action);
    }
    if (_action.type === InformationActionTypes.AddPreClosureBanner) {

      const payload = (<any>_action).payload;
      const savedSessionState = getSessionState(pre_closure_message_id);
      if (savedSessionState) {
        const mergedPayload = mergePayloadItem(payload, savedSessionState);
        const mergedAction = { type: _action.type, payload: mergedPayload } as unknown as AddPreClosureBanner;

        return _reducer(_state, mergedAction);
      }
      return _reducer(_state, _action);
    }
    if (_action.type === InformationActionTypes.DismissPreClosureBanner) {
      const savedSessionState = getSessionState(pre_closure_message_id);
      const updatedSessionState = _state.preClosureBanner.id !== savedSessionState ? _state.preClosureBanner.id : savedSessionState;
      sessionStorage.setItem(pre_closure_message_id, JSON.stringify(updatedSessionState));
 
      return _reducer(_state, _action);
    }

    return _reducer(_state, _action);
  };
}

function updateState(nextState: State, savedState: any, msgId: any): any {

  if (savedState && savedState?.length > 0) {
    if (savedState.some(msg => msg.id === msgId)) {
      return savedState;
    } else {
      savedState.push(msgId);
      return savedState;
    }
  }
  const msgIds = [];
  const _id = nextState.bannerMessages.some(x => x.id === msgId) ? nextState.bannerMessages.find(x => x.id === msgId).id : null;
  msgIds.push(_id);
  return msgIds;
}

function mergePayloadArray(payload: any[], savedState: any) {

  if (savedState && savedState?.length > 0 && payload && payload.length > 0) {
    const _mergedPayload = payload.map(x => (
      savedState.some(id => id === x.id) ? { ...x, isVisible: false } : x)
    );

    return _mergedPayload;
  }
  return payload;
}

function mergePayloadItem(payload: any, savedState: any) {

  if (savedState && payload) {
    const _mergedPayload =  savedState === payload.id ? { ...payload, isVisible: false } : payload;
    return _mergedPayload;
  }
  return payload;
}

export function reducer(_state: State | undefined, _action: InformationActions) {
  return updateStateReducer(persistStateReducer(stateReducer))(_state, _action);
}

export const getInformation = (state: State) => state.data;
export const getInformationLoaded = (state: State) => state.loaded;
export const getInformationLoading = (state: State) => state.loading;
export const getBannerMessages = (state: State) => state.bannerMessages;
export const getContextualBannerMessages = (state: State) => state.contextualBannerMessages;
export const getBannerMessagesLoaded = (state: State) => state.bannerMessagesLoaded;
export const getMessagesLoaded = (state: State) => state.bannerMessagesLoaded;
export const getPreClosureBanner = (state: State) => state.preClosureBanner;
export const getStartPageBanner = (state: State) => state.startPageBanner;
export const getAvtalInformation = (state: State) => state.data?.informations.filter(x => x.key === InformationKey.Avtal);
export const getAvtalFAQ = (state: State) => state.data?.informations.find(x => x.key === InformationKey.AvtalFAQ);
export const getAvtalsTillvalFAQFJV = (state: State) => state.data?.informations.find(x => x.key === InformationKey.AvtalstillvalFAQFJV);
export const getAvtalsTillvalFAQFJK = (state: State) => state.data?.informations.find(x => x.key === InformationKey.AvtalstillvalFAQFJK);
export const getMeteringPointInformations = (state: State) => state.data?.informations.filter(x => x.key === InformationKey.MeteringpointInfoGeneric || x.key === InformationKey.MeteringpointInfoIntelligy);
export const getMeteringPointInformationsIntelligy = (state: State) => state.data?.informations.filter(x => x.key === InformationKey.MeteringpointInfoIntelligy);
export const getIntelligyInformations = (state: State) => state.data?.informations.filter(x => x.key === InformationKey.ServicesIntelligy);



